import "./parent-child-modal.scss";
import {observer} from "mobx-react-lite";
import React, {useCallback, useEffect, useMemo} from "react";
import {DefaultModalLayout} from "../../../components/modal/layouts/default-modal-layout";
import {BUTTON_VARIANTS} from "../../../components/inputs/button-variants";
import FontBody1 from "../../../components/fonts/font-body1/font-body1";
import {FONT_COLOR, FONT_VARIANT} from "../../../components/fonts/font-props";
import PlanInfoAdmin from "../../manage-subscription-plans/components/assign-shops-confirm-modal/plan-info/plan-info-admin";
import ParentChildStore from "../stores/parent-child-store";
import ShopsSelector from "../../../components/shops-selector/shops-selector";
import ConnectedStores from "../modals/components/connected-stores"
import FontBody2 from "../../../components/fonts/font-body2/font-body2";
import ListLayout from "../../../components/layouts/list-layout/list-layout";
import LinearLoader from "../../../components/loaders/linear-loader/linear-loader";
import {Note, NOTE_TYPE} from "../../../components/note/note";
import { UserMasterListStore } from "../stores/user-master-list-store";
import { IShop } from "../../../models/User";

interface IParentChildModal {
    userId: number;
    onCloseClick: () => void;
    userMasterListStore: UserMasterListStore;
}

const ParentChildModal: React.FC<IParentChildModal> = observer(({ userId, onCloseClick, userMasterListStore }) => {
    const store = useMemo<ParentChildStore>(() => new ParentChildStore(userMasterListStore), [userMasterListStore]);

    useEffect(() => {
            store.load(userId);
        }, [userId, store]
    )

    const saveClick = () => {
        if (!store.hasChanged()) {
            return;
        }
        store.confirmChanges();
    }

    const onShopSelectorChange = useCallback((list: IShop[]) => store.setChildrenToAdd(list), [store]);
    const buttons = [];
    if (store.getSelectedUserPlan()) {
        buttons.push({
                text: 'Cancel',
                variant: BUTTON_VARIANTS.Tertiary,
                disabled: store.isLoading,
                onClick: onCloseClick
            },
            {
                text: 'Save',
                variant: BUTTON_VARIANTS.Primary,
                disabled: store.isSaving || store.isLoading || !store.hasChanged(),
                onClick: saveClick,
                hasLoader: true,
                isLoading: store.isSaving
            }
        )
    } 
    else {
        buttons.push({
                text: 'OK',
                variant: BUTTON_VARIANTS.Tertiary,
                disabled: store.isLoading,
                onClick: onCloseClick
            }
        )
    }

    const showSelector = store.isLoading || store.getSelectedUserPlan();

    return (
        <DefaultModalLayout
            isCloseDisabled={store.isLoading}
            onCloseClicked={onCloseClick}
            buttons={buttons}
        >
            <div className="parent-child-modal">
                {store.isLoading && <LinearLoader/>}
                <ListLayout>
                    {showSelector && <>
                        <FontBody1 color={FONT_COLOR.NEUTRAL3}>
                            {!store.selectedUserIsChild && `${store.getParent()?.name}`}
                            {store.selectedUserIsChild && `Parent store: ${store.getParent()?.name}`}
                        </FontBody1>
                        <div>
                            <PlanInfoAdmin plan={store.getSelectedUserPlan()}/>
                            <FontBody2 variant={FONT_VARIANT.BOLD}>Assign stores</FontBody2>
                            <ShopsSelector value={store.getChildrenToAdd()} disabled={store.selectedUserIsChild} onChange={onShopSelectorChange}/>
                            {store.getChildren() && <ConnectedStores store={store}/>}
                        </div>
                    </>
                    }
                    {!showSelector &&
                        <Note type={NOTE_TYPE.INFORMATION} body={
                            <FontBody1> {`Store was successfully removed from parent`} </FontBody1>
                        }/>
                    }
                </ListLayout>
            </div>
        </DefaultModalLayout>
    )
});

export default ParentChildModal;